import { createClient } from '@supabase/supabase-js';

// ── Replace these with your actual Supabase project credentials ──
// Found at: https://app.supabase.com → Project Settings → API
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://YOUR_PROJECT.supabase.co';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || 'YOUR_ANON_KEY';

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// ── Auth helpers ─────────────────────────────────────────────────
export const signUp = async (email, password, username) => {
  const { data, error } = await supabase.auth.signUp({
    email, password,
    options: { data: { username } }
  });
  return { data, error };
};

export const signIn = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  return { data, error };
};

export const signOut = () => supabase.auth.signOut();

export const getSession = () => supabase.auth.getSession();

// ── Profile helpers ──────────────────────────────────────────────
export const getProfile = async (userId) => {
  const { data } = await supabase
    .from('profiles').select('*').eq('id', userId).single();
  return data;
};

// ── Film helpers ─────────────────────────────────────────────────
export const getFilms = async () => {
  const { data } = await supabase
    .from('films').select('*').order('title');
  return data || [];
};

// Upload poster image to Supabase Storage, returns public URL
export const uploadFilmPoster = async (file, filmTitle) => {
  const ext = file.name.split('.').pop();
  const fileName = `${Date.now()}_${filmTitle.replace(/\s+/g, '_').toLowerCase()}.${ext}`;
  const { error } = await supabase.storage
    .from('film-posters')
    .upload(fileName, file, { cacheControl: '3600', upsert: false });
  if (error) return { url: null, error };
  const { data } = supabase.storage.from('film-posters').getPublicUrl(fileName);
  return { url: data.publicUrl, error: null };
};

// Insert a new film (admin only)
export const insertFilm = async (film) => {
  const { data, error } = await supabase.from('films').insert(film).select().single();
  return { data, error };
};

// Delete a film (admin only)
export const deleteFilm = async (filmId) => {
  const { error } = await supabase.from('films').delete().eq('id', filmId);
  return error;
};

// ── Rating helpers ────────────────────────────────────────────────
export const getFilmRatings = async (filmId) => {
  const { data } = await supabase
    .from('ratings').select('rating, user_id').eq('film_id', filmId);
  return data || [];
};

export const upsertRating = async (filmId, userId, rating) => {
  const { error } = await supabase.from('ratings').upsert(
    { film_id: filmId, user_id: userId, rating },
    { onConflict: 'film_id,user_id' }
  );
  return error;
};

// ── Genre vote helpers ────────────────────────────────────────────
export const getGenreVotes = async (filmId) => {
  const { data } = await supabase
    .from('genre_votes').select('genre, user_id').eq('film_id', filmId);
  return data || [];
};

export const upsertGenreVote = async (filmId, userId, genre) => {
  // Insert a new vote — allow multiple genres per user per film
  const { error } = await supabase.from('genre_votes')
    .upsert(
      { film_id: filmId, user_id: userId, genre },
      { onConflict: 'film_id,user_id,genre', ignoreDuplicates: true }
    );
  return error;
};

export const removeGenreVote = async (filmId, userId, genre) => {
  const { error } = await supabase.from('genre_votes')
    .delete()
    .eq('film_id', filmId)
    .eq('user_id', userId)
    .eq('genre', genre);
  return error;
};

// ── Review helpers ────────────────────────────────────────────────
export const getReviews = async (filmId) => {
  const { data } = await supabase
    .from('reviews')
    .select('*, profiles(username)')
    .eq('film_id', filmId)
    .order('created_at', { ascending: false });
  return data || [];
};

export const insertReview = async (filmId, userId, body) => {
  const { data, error } = await supabase.from('reviews')
    .insert({ film_id: filmId, user_id: userId, body }).select().single();
  return { data, error };
};

export const deleteReview = async (reviewId, userId) => {
  const { error } = await supabase.from('reviews')
    .delete().eq('id', reviewId).eq('user_id', userId); // RLS + double-check ownership
  return error;
};

// ── Report helpers ────────────────────────────────────────────────
export const submitReport = async ({ reviewId, reportedUserId, reportedBy, filmId, reason }) => {
  const { error } = await supabase.from('reports').insert({
    review_id: reviewId,
    reported_user_id: reportedUserId,
    reported_by: reportedBy,
    film_id: filmId,
    reason
  });
  return error;
};

// ── Ban helpers ───────────────────────────────────────────────────
export const getActiveBans = async () => {
  const { data } = await supabase
    .from('bans').select('*, profiles!bans_user_id_fkey(username)')
    .gt('ban_until', new Date().toISOString());
  return data || [];
};

export const isUserBanned = async (userId) => {
  const { data } = await supabase
    .from('bans').select('id')
    .eq('user_id', userId)
    .gt('ban_until', new Date().toISOString())
    .limit(1);
  return data && data.length > 0;
};

export const banUser = async (userId, bannedBy, reason, banUntil) => {
  const { error } = await supabase.from('bans').insert({
    user_id: userId, banned_by: bannedBy, reason, ban_until: banUntil
  });
  return error;
};

export const unbanUser = async (userId) => {
  const { error } = await supabase.from('bans')
    .delete().eq('user_id', userId);
  return error;
};

// ── Admin helpers ─────────────────────────────────────────────────
export const getAllUsers = async () => {
  const { data } = await supabase.from('profiles').select('*').order('created_at', { ascending: false });
  return data || [];
};

export const getAllReports = async () => {
  const { data } = await supabase
    .from('reports')
    .select('*, reporter:profiles!reports_reported_by_fkey(username), reported:profiles!reports_reported_user_id_fkey(username), films(title)')
    .order('created_at', { ascending: false });
  return data || [];
};

export const updateReportStatus = async (reportId, status) => {
  const { error } = await supabase.from('reports').update({ status }).eq('id', reportId);
  return error;
};

export const getFilmRequests = async () => {
  const { data } = await supabase.from('film_requests').select('*').order('created_at', { ascending: false });
  return data || [];
};

export const updateFilmRequestStatus = async (id, status) => {
  const { error } = await supabase.from('film_requests').update({ status }).eq('id', id);
  return error;
};

// ── Film request (EmailJS integration point) ──────────────────────
export const submitFilmRequest = async ({ filmName, language, releaseYear, requesterEmail, requestedBy, type }) => {
  const { error } = await supabase.from('film_requests').insert({
    film_name: filmName,
    language,
    release_year: releaseYear,
    requester_email: requesterEmail,
    requested_by: requestedBy || null,
    type: type || 'movie',
  });
  return error;
};

// ── Profile update helpers ────────────────────────────────────
export const updateUsername = async (userId, username) => {
  // Check if username already taken
  const { data: existing } = await supabase
    .from('profiles').select('id').eq('username', username).neq('id', userId).single();
  if (existing) return { error: { message: 'Username already taken.' } };

  const { error } = await supabase
    .from('profiles').update({ username }).eq('id', userId);
  return { error };
};

export const updatePassword = async (newPassword) => {
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  return { error };
};

// ── Chat helpers ──────────────────────────────────────────────
export const getChatMessages = async (limit = 80) => {
  const { data } = await supabase
    .from('chat_messages')
    .select('*, profiles(username, role)')
    .order('created_at', { ascending: true })
    .limit(limit);
  return data || [];
};

export const sendChatMessage = async (userId, message) => {
  const { error } = await supabase
    .from('chat_messages')
    .insert({ user_id: userId, message });
  return error;
};

export const deleteChatMessage = async (id) => {
  const { error } = await supabase
    .from('chat_messages')
    .delete().eq('id', id);
  return error;
};


// ── Poll helpers ──────────────────────────────────────────────
export const getActivePolls = async () => {
  const { data } = await supabase
    .from('chat_polls')
    .select('*, chat_poll_votes(*)')
    .eq('closed', false)
    .order('created_at', { ascending: false });
  return data || [];
};

export const createPoll = async (createdBy, question, options) => {
  const { error } = await supabase
    .from('chat_polls')
    .insert({ created_by: createdBy, question, options });
  return error;
};

export const removePollVote = async (pollId, userId) => {
  const { error } = await supabase
    .from('chat_poll_votes')
    .delete()
    .eq('poll_id', pollId)
    .eq('user_id', userId);
  return error;
};

export const votePoll = async (pollId, userId, optionIdx) => {
  const { error } = await supabase
    .from('chat_poll_votes')
    .upsert({ poll_id: pollId, user_id: userId, option_idx: optionIdx },
      { onConflict: 'poll_id,user_id' });
  return error;
};

export const closePoll = async (pollId) => {
  const { error } = await supabase
    .from('chat_polls')
    .update({ closed: true }).eq('id', pollId);
  return error;
};


// ── Presence helpers ──────────────────────────────────────────
export const updatePresence = async (userId) => {
  await supabase.from('chat_presence')
    .upsert({ user_id: userId, last_seen: new Date().toISOString() },
      { onConflict: 'user_id' });
};

export const getOnlineUsers = async () => {
  const cutoff = new Date(Date.now() - 2 * 60 * 1000).toISOString(); // 2 min window
  const { data } = await supabase
    .from('chat_presence')
    .select('user_id, last_seen, profiles(username, role)')
    .gt('last_seen', cutoff);
  return data || [];
};

// ── Rankings helpers ──────────────────────────────────────────
export const getRankedFilms = async ({ language, genre, year, type } = {}) => {
  let query = supabase
    .from('films')
    .select('*, ratings(rating)');
  if (language) query = query.eq('language', language);
  if (genre)    query = query.contains('genres', [genre]);
  if (year)     query = query.eq('year', year);
  if (type)     query = query.eq('type', type);
  const { data } = await query;
  if (!data) return [];
  return data
    .map(f => ({
      ...f,
      avgRating: f.ratings?.length
        ? f.ratings.reduce((s, r) => s + r.rating, 0) / f.ratings.length
        : 0,
      ratingCount: f.ratings?.length || 0,
    }))
    .filter(f => f.ratingCount > 0)
    .sort((a, b) => b.avgRating - a.avgRating);
};

// ── Battle helpers ────────────────────────────────────────────
export const getOrCreateBattle = async (userId) => {
  // Get films user hasn't voted on yet
  const { data: voted } = await supabase
    .from('battle_votes')
    .select('battle_id, battles(film_a_id, film_b_id)')
    .eq('user_id', userId);

  const votedFilmPairs = new Set(
    (voted || []).map(v => {
      const b = v.battles;
      return b ? `${Math.min(b.film_a_id, b.film_b_id)}-${Math.max(b.film_a_id, b.film_b_id)}` : null;
    }).filter(Boolean)
  );

  const { data: films } = await supabase.from('films').select('id, title, poster_url, poster_emoji, year, language');
  if (!films || films.length < 2) return null;

  // Pick two random films not yet battled by this user
  const shuffled = films.sort(() => Math.random() - 0.5);
  for (let i = 0; i < shuffled.length; i++) {
    for (let j = i + 1; j < shuffled.length; j++) {
      const key = `${Math.min(shuffled[i].id, shuffled[j].id)}-${Math.max(shuffled[i].id, shuffled[j].id)}`;
      if (!votedFilmPairs.has(key)) {
        // Check if this battle already exists
        const { data: existing } = await supabase
          .from('battles')
          .select('*')
          .or(`and(film_a_id.eq.${shuffled[i].id},film_b_id.eq.${shuffled[j].id}),and(film_a_id.eq.${shuffled[j].id},film_b_id.eq.${shuffled[i].id})`)
          .single();
        if (existing) return { battle: existing, filmA: shuffled[i], filmB: shuffled[j] };
        // Create new battle
        const { data: newBattle } = await supabase
          .from('battles')
          .insert({ film_a_id: shuffled[i].id, film_b_id: shuffled[j].id })
          .select().single();
        return { battle: newBattle, filmA: shuffled[i], filmB: shuffled[j] };
      }
    }
  }
  return null; // User has voted on all possible battles
};

export const submitBattleVote = async (battleId, userId, winnerId) => {
  const { error } = await supabase.from('battle_votes')
    .insert({ battle_id: battleId, user_id: userId, winner_id: winnerId });
  return error;
};

export const getFilmBattleStats = async (filmId) => {
  const { data: wins } = await supabase
    .from('battle_votes').select('id').eq('winner_id', filmId);
  const { data: battlesA } = await supabase
    .from('battles').select('id').eq('film_a_id', filmId);
  const { data: battlesB } = await supabase
    .from('battles').select('id').eq('film_b_id', filmId);
  const total = ((battlesA?.length || 0) + (battlesB?.length || 0));
  const winCount = wins?.length || 0;
  return { wins: winCount, total, winRate: total ? Math.round(winCount / total * 100) : 0 };
};

// ── Taste Profile helpers ─────────────────────────────────────
export const getUserTasteProfile = async (userId) => {
  const { data: ratings } = await supabase
    .from('ratings')
    .select('rating, films(id, title, language, genres, type, poster_url, poster_emoji)')
    .eq('user_id', userId)
    .gte('rating', 6); // Only count films they liked (6+)

  if (!ratings || ratings.length < 5) return null;

  const langCount = {}, genreCount = {}, typeCount = { movie: 0, series: 0 };
  let total = ratings.length;

  ratings.forEach(r => {
    const f = r.films;
    if (!f) return;
    langCount[f.language]  = (langCount[f.language]  || 0) + 1;
    typeCount[f.type || 'movie']++;
    (f.genres || []).forEach(g => { genreCount[g] = (genreCount[g] || 0) + 1; });
  });

  const topLangs   = Object.entries(langCount).sort((a,b) => b[1]-a[1]).slice(0,4)
    .map(([lang, count]) => ({ lang, pct: Math.round(count/total*100) }));
  const topGenres  = Object.entries(genreCount).sort((a,b) => b[1]-a[1]).slice(0,5)
    .map(([genre]) => genre);

  // Get all films user hasn't rated yet for recommendations
  const { data: allFilms } = await supabase.from('films').select('*');
  const ratedIds = new Set(ratings.map(r => r.films?.id));
  const unrated  = (allFilms || []).filter(f => !ratedIds.has(f.id));

  // Score unrated films by how well they match taste
  const recommended = unrated.map(f => {
    let score = 0;
    if (topLangs[0]?.lang === f.language) score += 3;
    if (topLangs[1]?.lang === f.language) score += 2;
    (f.genres || []).forEach(g => { if (topGenres.includes(g)) score += 1; });
    return { ...f, score };
  }).sort((a,b) => b.score - a.score).slice(0, 8);

  return {
    totalRated: total,
    topLangs,
    topGenres,
    moviePct: Math.round(typeCount.movie / total * 100),
    seriesPct: Math.round(typeCount.series / total * 100),
    recommended,
  };
};

// ── Battle redesign helpers ───────────────────────────────────
export const getTodaysBattles = async () => {
  const today = new Date().toISOString().split('T')[0];
  const { data } = await supabase
    .from('battles')
    .select('*, film_a:films!battles_film_a_id_fkey(*), film_b:films!battles_film_b_id_fkey(*), battle_votes(*)')
    .eq('battle_date', today)
    .eq('is_active', true)
    .order('created_at', { ascending: true });
  return data || [];
};

export const getAllActiveBattles = async () => {
  const { data } = await supabase
    .from('battles')
    .select('*, film_a:films!battles_film_a_id_fkey(*), film_b:films!battles_film_b_id_fkey(*), battle_votes(*)')
    .eq('is_active', true)
    .order('battle_date', { ascending: false })
    .order('created_at', { ascending: false })
    .limit(20);
  return data || [];
};

export const getBattleLeaderboard = async () => {
  const { data } = await supabase
    .from('battle_votes')
    .select('winner_id, films!battle_votes_winner_id_fkey(id, title, poster_url, poster_emoji, language)');
  if (!data) return [];
  const counts = {};
  data.forEach(v => {
    const id = v.winner_id;
    if (!counts[id]) counts[id] = { film: v.films, wins: 0 };
    counts[id].wins++;
  });
  return Object.values(counts).sort((a, b) => b.wins - a.wins).slice(0, 10);
};

export const createAdminBattle = async (filmAId, filmBId, adminId, isToday = true) => {
  const today = new Date().toISOString().split('T')[0];
  const { error } = await supabase.from('battles').insert({
    film_a_id: filmAId, film_b_id: filmBId,
    created_by: adminId,
    battle_date: isToday ? today : null,
    is_featured: true, is_active: true,
  });
  return error;
};

export const toggleBattle = async (battleId, isActive) => {
  const { error } = await supabase.from('battles').update({ is_active: isActive }).eq('id', battleId);
  return error;
};

export const getBattleSuggestions = async () => {
  const { data } = await supabase
    .from('battle_suggestions')
    .select('*, profiles(username)')
    .order('created_at', { ascending: false })
    .limit(50);
  return data || [];
};

export const submitBattleSuggestion = async (userId, suggestion) => {
  const { error } = await supabase.from('battle_suggestions')
    .insert({ user_id: userId, suggestion });
  return error;
};

export const deleteBattleSuggestion = async (id) => {
  const { error } = await supabase.from('battle_suggestions').delete().eq('id', id);
  return error;
};

// ── OMDB film auto-save ───────────────────────────────────────
// Called when a user rates/reviews an OMDB film for the first time
export const saveOMDBFilm = async (omdbFilm) => {
  // Check if already saved by imdb_id
  const { data: existing } = await supabase
    .from('films')
    .select('id')
    .eq('imdb_id', omdbFilm.imdb_id)
    .single();
  if (existing) return existing.id;

  // Insert new film
  const { data, error } = await supabase.from('films').insert({
    title:       omdbFilm.title,
    year:        omdbFilm.year,
    language:    omdbFilm.language,
    description: omdbFilm.description,
    poster_url:  omdbFilm.poster_url,
    poster_emoji: omdbFilm.poster_emoji || '🎬',
    imdb_rating: omdbFilm.imdb_rating,
    genres:      omdbFilm.genres || [],
    type:        omdbFilm.type || 'movie',
    imdb_id:     omdbFilm.imdb_id,
    film_cast:   omdbFilm.film_cast || [],
  }).select('id').single();

  if (error) return null;
  return data.id;
};

// ── Film enrichment ───────────────────────────────────────────
export const updateFilmMeta = async (filmId, meta) => {
  const { error } = await supabase.from('films').update({
    director:    meta.director    || null,
    runtime:     meta.runtime     || null,
    country:     meta.country     || null,
    awards:      meta.awards      || null,
    imdb_id:     meta.imdb_id     || null,
    film_cast:   meta.film_cast   || null,
    poster_url:  meta.poster_url  || null,
    description: meta.description || null,
    imdb_rating: meta.imdb_rating || null,
  }).eq('id', filmId);
  if (error) console.error('updateFilmMeta error:', error);
  return error;
};
